// useLive — single WebSocket per channel for chat / reactions / presence.
//
// One hook keeps the wire protocol contained in a single place:
//   - state: chatMessages, reactions, viewerCount, nick, status
//   - actions: sendChat(text), sendReaction(emoji), renameNick(nick)
// Reconnects automatically when the channel id changes; defers connection
// while disabled so we don't open a socket for virtual channels (Favourites,
// Continue Watching) where chat would feel out of place.
import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function buildWsUrl(channelId, nick, ownerToken) {
  // Convert https → wss / http → ws and append the API path.
  const base = BACKEND.replace(/^http/i, "ws");
  const params = new URLSearchParams();
  if (nick) params.set("nick", nick);
  if (ownerToken) params.set("owner_token", ownerToken);
  const qs = params.toString();
  return `${base}/api/ws/live/${encodeURIComponent(channelId)}${qs ? `?${qs}` : ""}`;
}

const MAX_CHAT_RETAINED = 30;
const NICK_STORAGE = "crt_live_nick";
const OWNER_TOKEN_STORAGE = "crt_owner_token";

export function useLive({ channelId, enabled = true, tunedChannel = null }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  // Per-channel viewer-count snapshot pushed by the server whenever any
  // viewer tunes / connects / disconnects. Keyed by channel UUID →
  // integer count. Drives the live "📺 N watching" badges in the EPG.
  const [channelCounts, setChannelCounts] = useState({});
  const [nick, setNick] = useState(() => {
    try { return localStorage.getItem(NICK_STORAGE) || ""; }
    catch (e) { return ""; }
  });
  const [isOwnerNick, setIsOwnerNick] = useState(false);
  // 'idle' before mount, 'connecting' while opening, 'open' once welcome
  // arrived, 'closed' after disconnect. UI uses this to decide whether to
  // grey out the chat input.
  const [status, setStatus] = useState("idle");

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);

  useEffect(() => {
    if (!enabled || !channelId) {
      setStatus("idle");
      setChatMessages([]);
      setViewerCount(0);
      setIsOwnerNick(false);
      setPinned(null);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
        wsRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let ownerToken = "";
    try { ownerToken = localStorage.getItem(OWNER_TOKEN_STORAGE) || ""; }
    catch (e) { /* noop */ }

    const open = () => {
      if (cancelled) return;
      setStatus("connecting");
      // Owners always reconnect under the canonical SCARTLEAD.TV handle —
      // ignore whatever happens to be in localStorage for them so a previous
      // rename can't "stick". Viewers keep their stored handle (or omit it
      // and let the server generate one).
      const effectiveNick = ownerToken ? "SCARTLEAD.TV" : nick;
      const ws = new WebSocket(buildWsUrl(channelId, effectiveNick, ownerToken));
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); }
        catch (e) { return; }
        if (!msg || !msg.type) return;
        if (msg.type === "welcome") {
          setStatus("open");
          setNick(msg.nick);
          setIsOwnerNick(!!msg.is_owner);
          try { localStorage.setItem(NICK_STORAGE, msg.nick); } catch (e) { /* noop */ }
          setViewerCount(msg.count || 0);
          setChatMessages(Array.isArray(msg.history) ? msg.history.slice(-MAX_CHAT_RETAINED) : []);
          setPinned(msg.pinned || null);
          if (msg.channel_counts && typeof msg.channel_counts === "object") {
            setChannelCounts(msg.channel_counts);
          }
        } else if (msg.type === "channel-counts") {
          setChannelCounts(msg.counts && typeof msg.counts === "object" ? msg.counts : {});
        } else if (msg.type === "site-status") {
          const next = msg.status && typeof msg.status === "object" ? msg.status : null;
          if (next) {
            try { window.dispatchEvent(new CustomEvent("crt:site-status", { detail: next })); }
            catch (e) { /* non-critical realtime enhancement */ }
          }
        } else if (msg.type === "pinned") {
          setPinned(msg.pinned || null);
        } else if (msg.type === "presence") {
          setViewerCount(msg.count || 0);
        } else if (msg.type === "chat") {
          setChatMessages((prev) => {
            const next = prev.concat(msg);
            return next.length > MAX_CHAT_RETAINED ? next.slice(-MAX_CHAT_RETAINED) : next;
          });
        } else if (msg.type === "nick_ok") {
          setNick(msg.nick);
          try { localStorage.setItem(NICK_STORAGE, msg.nick); } catch (e) { /* noop */ }
        } else if (msg.type === "nick_rejected") {
          // Server refused a reserved nick — surface via the existing chat
          // panel as a system line so the viewer knows why nothing changed.
          setChatMessages((prev) => prev.concat({
            ts: Date.now(),
            nick: "SYSTEM",
            is_owner: false,
            text: "That name is reserved — pick another.",
            system: true,
          }));
        }
      };

      ws.onclose = () => {
        setStatus("closed");
        wsRef.current = null;
        if (!cancelled) {
          reconnectTimer.current = setTimeout(open, 3000);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch (e) { /* noop */ }
      };
    };

    heartbeatTimer.current = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { /* noop */ }
      }
    }, 25000);

    open();

    return () => {
      cancelled = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (e) { /* noop */ }
        wsRef.current = null;
      }
    };
  // `nick` deliberately not in deps — renames re-use the open socket via the
  // server-side `nick` message instead of forcing a reconnect.
  }, [channelId, enabled]);

  const sendChat = useCallback((text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    const trimmed = (text || "").trim();
    if (!trimmed) return false;
    try {
      ws.send(JSON.stringify({ type: "chat", text: trimmed.slice(0, 220) }));
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const renameNick = useCallback((newNick) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify({ type: "nick", nick: newNick }));
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const pinMessage = useCallback((text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify({ type: "pin", text: text || "" }));
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // Emit a `tune` message whenever the viewer's currently-watched channel
  // changes. The backend uses this to maintain a live "viewers per
  // channel" snapshot and broadcasts the aggregated counts to every
  // lobby connection. We also re-emit whenever the socket transitions
  // to `open` so a reconnect immediately re-registers the viewer's
  // current channel without waiting for a manual channel-flip.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "tune", channel: tunedChannel || null }));
    } catch (e) { /* noop */ }
  }, [tunedChannel, status]);

  return { chatMessages, pinned, viewerCount, channelCounts, nick, isOwnerNick, status, sendChat, renameNick, pinMessage };
}
